import * as Sentry from "@sentry/react";
import {
  QueryClientProvider as BaseQueryClientProvider,
  MutationCache,
  QueryCache,
  QueryClient,
  experimental_streamedQuery,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as React from "react";
import { createContext } from "react-router";
import type { Static, TSchema } from "typebox";
import type { Validator } from "typebox/compile";
import {
  endReadRequest,
  endSaveRequest,
  failedSaveRequest,
  startReadRequest,
  startSaveRequest,
} from "@/shared/queries/networkState.ts";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";
import { isArrayOfLength } from "@/shared/util/invariant.ts";
import { isNotEmpty } from "@/shared/util/value.ts";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type PassedValidator<T extends TSchema> = Validator<{}, T>;

export const csrfTokenHeaderName = "X-CSRF-Token";
const pusherSessionIdHeaderName = "X-PUSHER-SESSION-ID";

interface TokenHeaders {
  [csrfTokenHeaderName]?: string;
  [pusherSessionIdHeaderName]?: string;
}

export function getCsrfToken(): string | undefined {
  const tokenElement = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  );

  return tokenElement?.content;
}

export function getTokenHeaders(): TokenHeaders {
  const tokenHeaders: TokenHeaders = {};

  const token = getCsrfToken();

  if (token) {
    tokenHeaders[csrfTokenHeaderName] = token;
  }

  const pusherSessionId = getCvPartner().subscriber?.session_id();

  if (pusherSessionId) {
    tokenHeaders[pusherSessionIdHeaderName] = pusherSessionId;
  }

  return tokenHeaders;
}

interface BaseFetchOptions<TBody extends object = object> {
  body?: TBody | FormData;
  safe?: boolean;
  timeout?: number;
  signal?: AbortSignal;
  skipLoadingIndicator?: boolean;
}

interface CheckedFetchOptions<
  TBody extends object = object,
> extends BaseFetchOptions<TBody> {
  plainText?: boolean;
}

interface FetchOptions<
  TBody extends object = object,
> extends CheckedFetchOptions<TBody> {
  ignoreResponseBody?: boolean;
}

type FetchMethod = "POST" | "GET" | "PUT" | "PATCH" | "DELETE";

function getRequestString(url: URL | string, method: string) {
  const urlInError =
    typeof url === "string"
      ? new URL(url, window.location.origin).pathname
      : url.pathname;

  return `${method} ${urlInError}`;
}

export class FetchError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: string | undefined;
  readonly parsedBody: unknown | undefined;

  constructor(
    method: string,
    url: URL | string,
    status: number,
    statusText: string,
    body: string | undefined,
    parsedBody: unknown | undefined,
  ) {
    const requestString = getRequestString(url, method);
    const formattedStatusText = statusText ? ` "${statusText}"` : "";

    super(
      `${requestString} failed with status ${status}${formattedStatusText}`,
    );

    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.parsedBody = parsedBody;
  }
}

export class FetchTimeout extends Error {
  constructor(method: FetchMethod, url: URL | string, timeout: number) {
    super(`${getRequestString(url, method)} timed out after ${timeout}ms`);
  }
}

export class FetchNetworkError extends Error {
  constructor(method: FetchMethod, url: URL | string, cause: unknown) {
    super(`${getRequestString(url, method)} got an error`, { cause });
  }
}

class FetchCancelError extends Error {
  constructor(method: FetchMethod, url: URL | string) {
    super(`${getRequestString(url, method)} was cancelled`);
  }
}

type Timeout = ReturnType<typeof globalThis.setTimeout>;

interface TimeoutOptions {
  signal: AbortSignal | undefined;
  timeoutId: Timeout | undefined;
}

interface InternalFetchOptions extends TimeoutOptions {
  url: URL | string;
  method: FetchMethod;
  headers: HeadersInit;
  body: string | FormData | undefined;
}

interface InternalFetchBufferedOptions extends InternalFetchOptions {
  plainText: boolean;
  ignoreResponseBody: boolean;
}

async function callFetch({
  url,
  method,
  headers,
  body,
  signal,
  timeoutId,
}: InternalFetchOptions): Promise<Response> {
  try {
    return await fetch(url, { method, headers, body, signal });
  } catch (error) {
    throw new FetchNetworkError(method, url, error);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

async function makeRequest(options: InternalFetchOptions) {
  const res = await callFetch(options);

  const { url, method } = options;

  if (!res.ok) {
    let body: string | undefined = undefined;
    let parsedBody: unknown | undefined = undefined;

    try {
      body = await res.text();

      const contentType = res.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        parsedBody = JSON.parse(body);
      }
    } catch {
      // ignore
    }

    throw new FetchError(
      method,
      url,
      res.status,
      res.statusText,
      body,
      parsedBody,
    );
  }

  if (res.status === 204) {
    return null;
  }

  return res;
}

async function makeBufferedRequest(options: InternalFetchBufferedOptions) {
  const res = await makeRequest(options);

  if (!res || options.ignoreResponseBody) {
    return null;
  }

  if (options.plainText) {
    return res.text();
  }

  return res.json();
}

function createTimeout(
  method: FetchMethod,
  url: URL | string,
  timeout: FetchOptions["timeout"],
): TimeoutOptions & { controller: AbortController | undefined } {
  let signal: AbortSignal | undefined = undefined;
  let timeoutId: Timeout | undefined = undefined;
  let controller: AbortController | undefined = undefined;

  if (timeout) {
    const actualController = new AbortController();
    controller = actualController;
    signal = controller.signal;

    timeoutId = globalThis.setTimeout(() => {
      actualController.abort(new FetchTimeout(method, url, timeout));
    }, timeout);
  }

  return { signal, timeoutId, controller };
}

async function performFetch<T = unknown, TBody extends object = object>(
  method: FetchMethod,
  url: URL | string,
  options: FetchOptions<TBody> = {},
): Promise<T> {
  const {
    timeout,
    plainText,
    ignoreResponseBody,
    safe,
    signal: incomingSignal,
    skipLoadingIndicator,
  } = options;

  const headers: HeadersInit = {
    ...getTokenHeaders(),
    accept: plainText ? "text/plain" : "application/json",
    // This is needed for Rails to detect that this is an XHR request
    "X-Requested-With": "XMLHttpRequest",
  };

  let body: string | FormData | undefined = undefined;

  if (options.body instanceof FormData) {
    body = options.body;
  } else if (
    options.body &&
    (method === "POST" || method === "PUT" || method === "PATCH")
  ) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const isReadRequest = method === "GET" || safe;

  if (!skipLoadingIndicator) {
    if (isReadRequest) {
      startReadRequest();
    } else {
      startSaveRequest();
    }
  }

  let { signal, timeoutId } = createTimeout(method, url, timeout);

  // if both a signal was passed, and we created a timeout signal, we need to combine them
  if (signal && incomingSignal) {
    signal = abortSignalAny([signal, incomingSignal]);
  }

  const request = makeBufferedRequest({
    url,
    method,
    headers,
    body,
    signal,
    timeoutId,
    plainText: plainText ?? false,
    ignoreResponseBody: ignoreResponseBody ?? false,
  });

  if (skipLoadingIndicator) {
    return request;
  }

  if (isReadRequest) {
    try {
      return await request;
    } finally {
      endReadRequest();
    }
  }

  try {
    const res = await request;

    endSaveRequest();

    return res;
  } catch (error) {
    failedSaveRequest();

    throw error;
  }
}

function bindFetch(method: FetchMethod) {
  return async function fetch<T, TBody extends object = object>(
    url: URL | string,
    options?: FetchOptions<TBody>,
  ) {
    return await performFetch<T, TBody>(method, url, options);
  };
}

export const http = {
  get: bindFetch("GET"),
  post: bindFetch("POST"),
  patch: bindFetch("PATCH"),
  put: bindFetch("PUT"),
  delete: bindFetch("DELETE"),
};

function bindCheckedFetch(method: FetchMethod) {
  return async function checkedFetch<
    T extends TSchema,
    TBody extends object = object,
  >(
    url: URL | string,
    checker: PassedValidator<T>,
    options?: CheckedFetchOptions<TBody>,
  ) {
    const fetchResult = await performFetch<unknown, TBody>(
      method,
      url,
      options,
    );

    return checker.Decode(fetchResult);
  };
}

export const httpChecked = {
  get: bindCheckedFetch("GET"),
  post: bindCheckedFetch("POST"),
  patch: bindCheckedFetch("PATCH"),
  put: bindCheckedFetch("PUT"),
  delete: bindCheckedFetch("DELETE"),
};

/**
 * Use this if you're unsure if the response is valid for the types you've defined.
 */
function bindWarningCheckedFetch(method: FetchMethod) {
  return async function checkedFetch<
    T extends TSchema,
    TBody extends object = object,
  >(
    url: URL | string,
    checker: PassedValidator<T>,
    options?: CheckedFetchOptions<TBody>,
  ) {
    const fetchResult = await performFetch<unknown, TBody>(
      method,
      url,
      options,
    );

    if (checker.Check(fetchResult)) {
      return fetchResult;
    }

    const validationErrors = [...checker.Errors(fetchResult)];
    Sentry.captureException(new Error(`${url} - Failed to decode response`), {
      tags: { url: url.toString() },
      extra: { fetchResult, validationErrors },
    });

    return fetchResult as Static<T>;
  };
}

export const httpWarnChecked = {
  get: bindWarningCheckedFetch("GET"),
  post: bindWarningCheckedFetch("POST"),
  patch: bindWarningCheckedFetch("PATCH"),
  put: bindWarningCheckedFetch("PUT"),
  delete: bindWarningCheckedFetch("DELETE"),
};

export async function ignoreNotFound<T>(promise: Promise<T | true>) {
  try {
    return await promise;
  } catch (error) {
    if (error instanceof FetchError && error.status === 404) {
      return true;
    }
    throw error;
  }
}

interface BaseStreamedRequestResult {
  cancel: () => void;
}

export type StreamedRequestResult = BaseStreamedRequestResult &
  (
    | {
        isFetching: false;
        isError: true;
        isDone: false;
        hasData: true;
        data: undefined;
      }
    | {
        isFetching: true;
        isError: false;
        isDone: false;
        hasData: true;
        data: string;
      }
    | {
        isFetching: false;
        isError: false;
        isDone: true;
        hasData: true;
        data: string;
      }
    | {
        isFetching: false;
        isError: false;
        isDone: true;
        hasData: false;
        data: undefined;
      }
  );

const streamMethod = "POST";

const streamingQuery = (url: string, body: Record<string, unknown>) =>
  queryOptions({
    queryKey: ["streaming", url, body] as const,
    queryFn: experimental_streamedQuery({
      async *streamFn({ signal: querySignal }) {
        startReadRequest();

        try {
          const {
            signal: timeoutSignal,
            timeoutId,
            controller,
          } = createTimeout(streamMethod, url, 20_000);

          const signal = timeoutSignal
            ? abortSignalAny([timeoutSignal, querySignal])
            : querySignal;

          const response = await makeRequest({
            url,
            method: streamMethod,
            headers: {
              ...getTokenHeaders(),
              "content-type": "application/json",
            },
            body: JSON.stringify(body),
            signal,
            timeoutId,
          });

          if (!response || !response.body) {
            throw new Error("Did not get a response body when streaming");
          }

          const decoder = new TextDecoder();

          // Only Firefox and Node has async iterator support for ReadableStream :(
          // Chromium got it in 124, but Safari is still missing it.
          // Safari tracking bug: https://bugs.webkit.org/show_bug.cgi?id=194379
          // https://developer.mozilla.org/en-US/docs/Web/API/ReadableStream#browser_compatibility
          // for await (const chunk of response.body) {
          //   this.resultString += decoder.decode(chunk.value);
          // }

          const reader = response.body.getReader();

          let cancellationError: FetchCancelError | undefined = undefined;

          signal.addEventListener("abort", () => {
            cancellationError = new FetchCancelError(streamMethod, url);
            controller?.abort(cancellationError);
          });

          while (true) {
            if (cancellationError) {
              await reader.cancel(cancellationError);
              break;
            }

            const chunk = await reader.read();

            if (chunk.done) {
              break;
            }

            yield decoder.decode(chunk.value);
          }

          reader.releaseLock();
        } finally {
          endReadRequest();
        }
      },
      reducer: (acc, chunk) => {
        // the first chunk might start with whitespace
        if (acc.length === 0) {
          return chunk.trimStart();
        }

        return acc + chunk;
      },
      initialValue: "",
    }),
  });

type StreamedRequestHookResult = StreamedRequestResult & {
  start: (body: Record<string, unknown>) => void;
};

export function useStreamedRequest(url: string): StreamedRequestHookResult {
  const queryClient = useQueryClient();
  const [body, setBody] = React.useState<Record<string, unknown> | null>(null);

  const queryOptions = streamingQuery(url, body ?? {});

  const { data, isFetching, isError, isEnabled } = useQuery({
    ...queryOptions,
    staleTime: Infinity,
    enabled: body != null,
  });

  const start = (body: Record<string, unknown>) => {
    void Promise.all([
      queryClient.cancelQueries(queryOptions),
      queryClient.invalidateQueries(queryOptions),
    ]);

    setBody(body);
  };

  if (!isEnabled) {
    return {
      isFetching: false,
      isError: false,
      isDone: true,
      hasData: false,
      data: undefined,
      start,
      cancel: () => {},
    };
  }

  const cancel = () => {
    setBody(null);

    void queryClient.cancelQueries(queryOptions);
  };

  if (isError) {
    return {
      isFetching: false,
      isError: true,
      isDone: false,
      hasData: true,
      data: undefined,
      start,
      cancel,
    };
  }

  const string = data ?? "";

  if (isFetching) {
    return {
      isFetching: true,
      isError: false,
      isDone: false,
      hasData: true,
      data: string,
      start,
      cancel,
    };
  }

  return {
    isFetching: false,
    isError: false,
    isDone: true,
    hasData: true,
    data: string.trimEnd(),
    start,
    cancel,
  };
}

// TODO: remove export
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time defaults to 0 - giving it a higher value lets minor
      // waterfall requests be served from cache
      // https://tanstack.com/query/v5/docs/framework/react/guides/important-defaults
      staleTime: 150,
    },
  },
  queryCache: new QueryCache({
    onError: (error, { queryKey, queryHash, meta }) => {
      if (meta?.skipErrorLogging === true) return;
      const extra = { queryKey };
      extractTypeBoxError(error, extra);

      getCvPartner().faro?.api.pushError(error as Error, {
        context: { queryHash },
      });

      Sentry.captureException(error, {
        extra,
        tags: { query: queryKey.at(0)?.toString() ?? "unknown" },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _, __, { meta, options: { mutationKey } }) => {
      if (meta?.skipErrorLogging === true) return;

      const extra = { mutationKey };
      extractTypeBoxError(error, extra);

      getCvPartner().faro?.api.pushError(error as Error, {
        context: {
          mutation:
            mutationKey?.map((k) => JSON.stringify(k)).join(",") ?? "unknown",
        },
      });

      Sentry.captureException(error, {
        extra,
        tags: { mutation: mutationKey?.at(0)?.toString() ?? "unknown" },
      });
    },
  }),
});

function extractTypeBoxError(error: unknown, extra: Record<string, unknown>) {
  if (error && typeof error === "object" && "error" in error) {
    extra.error = error.error;
  }
}

// Not supported in all browsers: https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/any_static#browser_compatibility
function abortSignalAny(incomingSignals: readonly (AbortSignal | undefined)[]) {
  const signals = incomingSignals.filter(isNotEmpty);

  if (typeof AbortSignal.any === "function") {
    return AbortSignal.any(signals);
  }

  if (signals.length === 0) {
    const controller = new AbortController();
    return controller.signal;
  }

  if (isArrayOfLength(signals, 1)) return signals[0];

  for (const signal of signals) {
    if (signal.aborted) return signal;
  }

  const controller = new AbortController();

  const aborters: (() => void)[] = [];

  for (const signal of signals) {
    const abort = () => {
      controller.abort(signal.reason);

      for (const aborter of aborters) aborter();
    };

    signal.addEventListener("abort", abort);
    aborters.push(() => signal.removeEventListener("abort", abort));
  }

  return controller.signal;
}

export const QueryClientProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <BaseQueryClientProvider client={queryClient}>
    {children}
  </BaseQueryClientProvider>
);

// eslint-disable-next-line @eslint-react/naming-convention-context-name
export const queryClientContext = createContext(queryClient);
